import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PrivacyNoticeLink from './PrivacyNoticeLink';

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:7071';
const WORDING: string =
  import.meta.env.VITE_WHATSAPP_OPTIN_WORDING ||
  'I agree to receive study invitations and related messages from Cutest on WhatsApp. Reply STOP at any time to opt out.';

const EXPIRED_MESSAGE =
  'This link has expired. Reply to any of our emails or ask at reception for a new one.';
const GENERIC_ERROR = 'Something went wrong — please try again later.';

const POST_ERRORS: Record<string, string> = {
  'consent-required': 'Please tick the box to confirm you agree.',
  'mobile-required': 'Please enter your mobile number.',
  'mobile-invalid': 'That doesn\'t look like a valid UK mobile number. Please check it and try again.',
  'mobile-landline': 'That looks like a landline. Please enter a UK mobile number (starting 07).',
  // 429: the number on this record has already been changed twice on this page in the last 24 hours.
  'too-many-number-changes':
    'This mobile number has already been changed twice today, so we can\'t change it again just now. Please try again tomorrow, or ask at reception to update it for you.',
  // 429: too many attempts from this connection in a minute.
  'rate-limited': 'Too many attempts. Please wait a minute and try again.',
};

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; wording: string | null; maskedMobile: string; alreadyOptedIn: boolean; previouslyOptedOut: string | null }
  | { status: 'expired' }
  | { status: 'failed' };

function OptInPage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [useDifferent, setUseDifferent] = useState(false);
  const [mobile, setMobile] = useState('');
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(''); // Honeypot - should remain empty
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/optInWhatsApp?token=${encodeURIComponent(token)}`);
        const data = await response.json().catch(() => null);
        if (cancelled) return;
        if (response.status === 403) {
          setLoad({ status: 'expired' });
        } else if (response.ok && data?.ok) {
          setLoad({
            status: 'ready',
            wording: typeof data.wording === 'string' && data.wording ? data.wording : null,
            maskedMobile: data.maskedMobile || '',
            alreadyOptedIn: !!data.alreadyOptedIn,
            previouslyOptedOut: data.previouslyOptedOut || null,
          });
        } else {
          setLoad({ status: 'failed' });
        }
      } catch {
        if (!cancelled) setLoad({ status: 'failed' });
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const noMobileOnFile = load.status === 'ready' && !load.maskedMobile;
  const showMobileInput = noMobileOnFile || useDifferent;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!consent) {
      setError(POST_ERRORS['consent-required']);
      return;
    }
    if (showMobileInput && !mobile.trim()) {
      setError(POST_ERRORS['mobile-required']);
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/optInWhatsApp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          mobile: showMobileInput ? mobile.trim() : undefined,
          consent: true,
          website,
        }),
      });
      const data = await response.json().catch(() => null);
      if (response.ok && data?.ok) {
        navigate('/optin/done');
      } else if (response.status === 403) {
        setLoad({ status: 'expired' });
      } else if ((response.status === 400 || response.status === 429) && data?.error && POST_ERRORS[data.error]) {
        setError(POST_ERRORS[data.error]);
      } else {
        setError(GENERIC_ERROR);
      }
    } catch {
      setError(GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  if (load.status === 'loading') {
    return (
      <div className="form-container">
        <div className="form-body">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (load.status === 'expired') {
    return (
      <div className="form-container">
        <div className="form-body">
          <p>{EXPIRED_MESSAGE}</p>
        </div>
      </div>
    );
  }

  if (load.status === 'failed') {
    return (
      <div className="form-container">
        <div className="form-body">
          <p>{GENERIC_ERROR}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <h1>Study alerts on WhatsApp</h1>
        {load.alreadyOptedIn && <p>You're already signed up. You can confirm or update your number below.</p>}
      </div>

      <form className="form-body" onSubmit={handleSubmit}>
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <p id="consent-wording">{load.wording || WORDING}</p>
          <PrivacyNoticeLink />
        </div>

        {!noMobileOnFile && (
          <div className="form-group">
            <label>Your mobile number</label>
            <p>{load.maskedMobile}</p>
            {!useDifferent && (
              <button type="button" style={{ background: "none", border: "none", padding: 0, color: "inherit", textDecoration: "underline", cursor: "pointer" }} onClick={() => setUseDifferent(true)}>
                Use a different number
              </button>
            )}
          </div>
        )}

        {showMobileInput && (
          <div className="form-group">
            <label htmlFor="mobile">{noMobileOnFile ? 'Your mobile number' : 'Different mobile number'}</label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="07700 900123"
              autoComplete="tel"
            />
          </div>
        )}

        {/* Honeypot field - hidden from users, bots will fill it */}
        <div className="honeypot-field" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input
            type="text"
            id="website"
            name="website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {load.previouslyOptedOut && (
          <div className="form-group">
            <p>
              You opted out of WhatsApp alerts on {load.previouslyOptedOut}. Ticking the box below opts you back in.
            </p>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="consent">
            <input
              type="checkbox"
              id="consent"
              name="consent"
              aria-describedby="consent-wording"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              required
            />{' '}
            I agree
          </label>
        </div>

        <button type="submit" className="submit-btn" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
    </div>
  );
}

export default OptInPage;
